/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"
import { useState } from "react"
import { generatePath, useNavigate } from "react-router"

import type { ApplicationType } from "../../features/application-type/application-type-types"
import { ApplicationTypeViewModel } from "../../features/application-type/application-type-viewmodel"
import { OrganizationViewModel } from "../../features/organization/organization-viewmodel"
import { AppRoutes } from "../../routes/routes"
import { useViewModel } from "../app/app-hooks"
import { ApplicationTypeCreateModal } from "../application-type/application-type-create-modal"
import { ApplicationTypeRowView } from "../application-type/application-type-row-view"

export const AllApplicationTypesView = observer(() => {
    const [showCreateModal, setShowCreateModal] = useState(false)

    const navigate = useNavigate()

    const vm = useViewModel(ApplicationTypeViewModel)
    const orgVM = useViewModel(OrganizationViewModel)

    const handleAppTypeClick = (appType: ApplicationType) => {
        navigate(generatePath(AppRoutes.ApplicationTypeDetail, { id: `${appType.id}` }))
    }

    const handleModalClose = () => {
        setShowCreateModal(false)
    }

    const handleModalSuccess = () => {
        setShowCreateModal(false)
        // Refresh the application list
        void vm.getAllAppTypes()
    }

    return (
        <div className="w-content col-xxl-6 col-xl-8 col-lg-10 col-md-12">
            <div className="flex justify-between items-center mb-6">
                <h3 className="title mb-0">All Applications</h3>
                <button className="btn w-button" onClick={() => setShowCreateModal(true)}>
                    Create Application
                </button>
            </div>

            {showCreateModal && (
                <ApplicationTypeCreateModal
                    organizations={orgVM.organizations}
                    selectedOrgId={orgVM.selectedOrgId === "none" ? -1 : Number(orgVM.selectedOrgId)}
                    onClose={handleModalClose}
                    onSuccess={handleModalSuccess}
                />
            )}

            {!vm.hasAllAppTypes && renderNoAppTypesFound(() => setShowCreateModal(true))}
            {vm.hasAllAppTypes && (
                <div className="card shadow-legacy-sm overflow-hidden">
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <tbody>
                                {vm.allAppTypes.map((appType) => (
                                    <ApplicationTypeRowView
                                        appType={appType}
                                        key={appType.id ?? 0}
                                        selected={false}
                                        selectable={false}
                                        onClick={handleAppTypeClick}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    )
})

function renderNoAppTypesFound(createHandler: () => void) {
    return (
        <div className="mt-12 mb-12">
            Sorry. No application types were found. You can start by{" "}
            <span
                role="button"
                className="text-legacy-primary underline"
                style={{ cursor: "pointer" }}
                onClick={createHandler}
            >
                creating a new one
            </span>
            {"."}
        </div>
    )
}
